/**
 * KeywordSetupPanel
 * Step 1 of the two-panel Market Insights flow.
 * Allows users to review, edit, and confirm seed keywords before analysis.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings2, RefreshCw, Plus, X, Check, Loader2, 
  ChevronDown, ChevronUp, AlertCircle, Sparkles,
  TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Coverage pill colors
function getCoverageClass(coverage) {
  switch (coverage) {
    case 'good': return 'bg-green-100 text-green-700 border-green-200';
    case 'low': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'none': return 'bg-slate-100 text-slate-500 border-slate-200';
    case 'error': return 'bg-red-100 text-red-600 border-red-200';
    default: return 'bg-slate-100 text-slate-500 border-slate-200';
  }
}

function getCoverageIcon(coverage) {
  switch (coverage) {
    case 'good': return <TrendingUp className="w-3 h-3" />;
    case 'low': return <Minus className="w-3 h-3" />;
    case 'none': return <TrendingDown className="w-3 h-3" />;
    default: return null;
  }
}

function formatVolume(vol) {
  if (!vol) return '0';
  if (vol >= 1000000) return `${(vol/1000000).toFixed(1)}M`;
  if (vol >= 1000) return `${(vol/1000).toFixed(1)}k`;
  return String(vol);
}

function SeedChip({ seed, validation, included, onToggle, onRemove }) {
  const coverage = validation?.coverage || 'none';
  const volume = validation?.volume || 0;
  
  return (
    <div 
      className={`group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all ${
        included 
          ? 'bg-white border-blue-200 shadow-sm' 
          : 'bg-slate-50 border-slate-200 opacity-60'
      }`}
    >
      {/* Include/Exclude checkbox */}
      <button
        onClick={onToggle}
        className={`w-4 h-4 rounded flex items-center justify-center transition-colors ${
          included 
            ? 'bg-blue-500 text-white' 
            : 'bg-slate-200 text-slate-400 hover:bg-slate-300'
        }`}
        data-testid={`seed-toggle-${seed.replace(/\s+/g, '-')}`}
      >
        {included && <Check className="w-2.5 h-2.5" />}
      </button>
      
      {/* Seed text */}
      <span className={`text-sm ${included ? 'text-slate-800' : 'text-slate-500 line-through'}`}>
        {seed}
      </span>
      
      {/* Coverage pill */}
      {validation && (
        <span className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border ${getCoverageClass(coverage)}`}>
          {getCoverageIcon(coverage)}
          <span className="capitalize">{coverage}</span>
          {volume > 0 && <span className="text-[10px] opacity-75">({formatVolume(volume)})</span>}
        </span>
      )}
      
      {/* Remove button */}
      <button
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 ml-0.5 text-slate-400 hover:text-red-500 transition-opacity"
        data-testid={`seed-remove-${seed.replace(/\s+/g, '-')}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function KeywordSetupPanel({
  businessAnalysis,
  qualifiers,
  channelFocus = 'search',
  websiteId,
  onSeedsConfirmed,
  isComplete = false,
  confirmedSeeds = []
}) {
  const [expanded, setExpanded] = useState(!isComplete);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [seeds, setSeeds] = useState([]);
  const [validations, setValidations] = useState({});
  const [includedSeeds, setIncludedSeeds] = useState(new Set());
  const [newSeed, setNewSeed] = useState('');
  const [showAddInput, setShowAddInput] = useState(false);
  const [meta, setMeta] = useState({});
  const [hasGenerated, setHasGenerated] = useState(false);
  
  // Initialize from confirmed seeds if provided
  useEffect(() => {
    if (isComplete && confirmedSeeds.length > 0) {
      setSeeds(confirmedSeeds);
      setIncludedSeeds(new Set(confirmedSeeds));
      setHasGenerated(true);
    }
  }, [isComplete, confirmedSeeds]);
  
  // Generate seed suggestions
  const generateSeeds = useCallback(async () => {
    if (!businessAnalysis || Object.keys(businessAnalysis).length === 0) {
      toast.error('Business analysis data required');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/insights/seeds/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          businessAnalysis,
          qualifiers: qualifiers || {},
          channelFocus,
          market: { country: 'US', language: 'en' }
        })
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      const suggestedSeeds = data.seeds || [];
      setSeeds(suggestedSeeds);
      setIncludedSeeds(new Set(suggestedSeeds));
      setMeta({
        businessType: data.businessType,
        geoMode: data.geoMode,
        vertical: data.vertical,
        serviceAnchors: data.serviceAnchors
      });
      setHasGenerated(true);
      
      // Auto-validate seeds
      if (suggestedSeeds.length > 0) {
        validateSeeds(suggestedSeeds);
      }
      
    } catch (error) {
      console.error('Failed to generate seeds:', error);
      toast.error('Failed to generate keyword suggestions');
    } finally {
      setLoading(false);
    }
  }, [businessAnalysis, qualifiers, channelFocus]);
  
  // Validate seeds for coverage
  const validateSeeds = async (seedsToValidate) => {
    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/insights/seeds/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          seeds: seedsToValidate,
          market: { country: 'US', language: 'en' }
        })
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      // Build validation map
      const validationMap = {};
      for (const v of data.validations || []) {
        validationMap[v.seed] = v;
      }
      setValidations(prev => ({ ...prev, ...validationMap }));
      
    } catch (error) {
      console.error('Failed to validate seeds:', error);
      // Don't show error toast - validation is optional
    } finally {
      setValidating(false);
    }
  };
  
  // Toggle seed inclusion
  const toggleSeed = (seed) => {
    setIncludedSeeds(prev => {
      const next = new Set(prev);
      if (next.has(seed)) {
        next.delete(seed);
      } else {
        next.add(seed);
      }
      return next;
    });
  };
  
  // Remove seed
  const removeSeed = (seed) => {
    setSeeds(prev => prev.filter(s => s !== seed));
    setIncludedSeeds(prev => {
      const next = new Set(prev);
      next.delete(seed);
      return next;
    });
  };
  
  // Add new seed
  const addSeed = () => {
    const trimmed = newSeed.trim().toLowerCase();
    if (!trimmed) return;
    
    if (seeds.includes(trimmed)) {
      toast.error('Seed already exists');
      return;
    }
    
    setSeeds(prev => [...prev, trimmed]);
    setIncludedSeeds(prev => new Set([...prev, trimmed]));
    setNewSeed('');
    setShowAddInput(false);
    
    // Validate the new seed
    validateSeeds([trimmed]);
  };
  
  // Confirm seeds and trigger insights generation
  const confirmSeeds = () => {
    const confirmed = seeds.filter(s => includedSeeds.has(s));
    if (confirmed.length === 0) {
      toast.error('Please include at least one seed keyword');
      return;
    }
    
    onSeedsConfirmed(confirmed);
    setExpanded(false);
  };
  
  // Count seeds by coverage
  const coverageCounts = {
    good: seeds.filter(s => includedSeeds.has(s) && validations[s]?.coverage === 'good').length,
    low: seeds.filter(s => includedSeeds.has(s) && validations[s]?.coverage === 'low').length,
    none: seeds.filter(s => includedSeeds.has(s) && (!validations[s] || validations[s]?.coverage === 'none')).length
  };
  
  const includedCount = includedSeeds.size;
  
  return (
    <div 
      className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
      data-testid="keyword-setup-panel"
    >
      {/* Header */}
      <div 
        className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
          isComplete ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'hover:bg-slate-50'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isComplete 
              ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
              : 'bg-gradient-to-br from-purple-100 to-purple-200'
          }`}>
            <Settings2 className={`w-5 h-5 ${isComplete ? 'text-white' : 'text-purple-600'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800">Keyword Setup</h3>
              {isComplete && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1 border border-green-200">
                  <Check className="w-3 h-3" />Confirmed
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              {hasGenerated
                ? `${includedCount} seeds selected • ${coverageCounts.good} good coverage`
                : 'Review and confirm seed keywords for analysis'
              }
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </div>
      
      {/* Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Generate button (if not yet generated) */}
          {!hasGenerated && (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-slate-800 mb-1">Generate Seed Keywords</h4>
              <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                We'll analyze your business context to suggest high-relevance keywords for market research.
              </p>
              <Button 
                onClick={generateSeeds}
                disabled={loading}
                className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Keywords
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* Seeds list */}
          {hasGenerated && (
            <>
              {/* Meta info */}
              {meta.businessType && (
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded">
                    {meta.businessType}
                  </span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded">
                    {meta.geoMode}
                  </span>
                  {meta.vertical && meta.vertical !== 'generic' && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                      {meta.vertical.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              )}
              
              {/* Coverage summary */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <span className="text-xs font-medium text-slate-600">Coverage:</span>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-xs text-green-700">
                    <TrendingUp className="w-3 h-3" />
                    {coverageCounts.good} Good
                  </span>
                  <span className="flex items-center gap-1 text-xs text-amber-700">
                    <Minus className="w-3 h-3" />
                    {coverageCounts.low} Low
                  </span>
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <TrendingDown className="w-3 h-3" />
                    {coverageCounts.none} None
                  </span>
                </div>
                {validating && (
                  <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-auto" />
                )}
              </div>
              
              {/* Seeds grid */}
              <div className="flex flex-wrap gap-2">
                {seeds.map(seed => (
                  <SeedChip
                    key={seed}
                    seed={seed}
                    validation={validations[seed]}
                    included={includedSeeds.has(seed)}
                    onToggle={() => toggleSeed(seed)}
                    onRemove={() => removeSeed(seed)}
                  />
                ))}
                
                {/* Add seed button/input */}
                {showAddInput ? (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-full border border-blue-200 bg-blue-50">
                    <Input
                      value={newSeed}
                      onChange={(e) => setNewSeed(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addSeed()}
                      placeholder="New keyword..."
                      className="h-6 w-32 text-sm border-0 bg-transparent p-0 focus-visible:ring-0"
                      autoFocus
                      data-testid="new-seed-input"
                    />
                    <button
                      onClick={addSeed}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setShowAddInput(false); setNewSeed(''); }}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddInput(true)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-blue-300 hover:text-blue-600 transition-colors"
                    data-testid="add-seed-button"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span className="text-sm">Add</span>
                  </button>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={generateSeeds}
                  disabled={loading}
                >
                  <RefreshCw className={`w-3 h-3 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                  Regenerate
                </Button>
                
                <Button
                  onClick={confirmSeeds}
                  disabled={includedCount === 0}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  data-testid="confirm-seeds-button"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Confirm & Generate Insights ({includedCount})
                </Button>
              </div>
              
              {/* Warning for low coverage */}
              {coverageCounts.good === 0 && includedCount > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-700">
                    <span className="font-semibold">Low keyword coverage warning.</span>
                    <span className="ml-1">
                      None of your selected seeds have good search volume. 
                      Consider adding more general keywords for better insights.
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
